import Snackbar from '@/components/elements/notifications/Snackbar/Snackbar';
import { isNationalId } from '@/helpers/utilsTS';
import { _t } from '@/locale';
import { getGiftcardCode, refundGiftcard, sendGiftcardReceipt } from '@/services/giftCardServices';
import { getGiftCard } from '@/services/useGiftCardsServices';
import * as Sentry from '@sentry/react';
import { useEffect, useReducer } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { giftCardReducer } from './GiftcardSelfServe.reducer';
import { giftcardTypeDefiniteLabel, giftcardTypeLabel } from './helpers/translations';
import { refundErrorSchema } from './types/schemas';

const baseTranslation = 'features.giftcard-self-serve.GiftcardSelfServe';

const useGiftcardSelfServe = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location?.search);

  const [state, dispatch] = useReducer(giftCardReducer, {
    codeInput: { error: '', value: '' },
    ssnInput: { error: '', value: '' },
    modalView: null,
    giftCard: null,
    loading: false,
    returnError: null,
    confirmLoading: false,
  });

  useEffect(() => {
    if (queryParams.has('token')) {
      getGiftcardCode(queryParams.get('token'))
        .then((res) => {
          dispatch({ type: 'SET_CODE', payload: res.code });
        })
        .catch((error) => {
          Sentry.captureException(error);
        });
    }

    if (queryParams.has('code')) {
      dispatch({ type: 'SET_CODE', payload: queryParams.get('code') });
    }
  }, []);

  const handleChangeCode = (newCode: string) => {
    dispatch({ type: 'SET_CODE', payload: newCode.trim() });
  };

  const handleChangeSsn = (newSsn: string) => {
    dispatch({ type: 'SET_SSN', payload: newSsn.trim() });
  };

  const handleSendReceipt = async () => {
    if (state.ssnInput.value && !isNationalId(state.ssnInput.value)) {
      dispatch({ type: 'SET_SSN_ERROR', payload: _t(`${baseTranslation}.error.invalidSsn`) });
      return;
    }

    dispatch({ type: 'SET_CONFIRM_LOADING' });

    try {
      await sendGiftcardReceipt(state.codeInput.value, state.ssnInput.value);
      dispatch({ type: 'SET_MODAL_VIEW', payload: 'receiptConfirmation' });
    } catch (error) {
      console.log(error);
    }
  };

  const handleRefund = async () => {
    dispatch({ type: 'SET_CONFIRM_LOADING' });

    try {
      await refundGiftcard(state.codeInput.value);
      dispatch({ type: 'SET_MODAL_VIEW', payload: 'refundConfirmation' });
    } catch (error) {
      const parsed = refundErrorSchema.safeParse(error);

      if (parsed.success) {
        const { errorCode } = parsed.data;
        switch (errorCode) {
          case 2:
            dispatch({
              type: 'SET_RETURN_ERROR',
              payload: _t(`${baseTranslation}.error.returned.2`, {
                type: giftcardTypeLabel(state.giftCard?.type).toLocaleLowerCase(),
              }),
            });
            break;
          case 3:
            dispatch({
              type: 'SET_RETURN_ERROR',
              payload: _t(`${baseTranslation}.error.returned.3`, {
                type: giftcardTypeLabel(state.giftCard?.type),
              }),
            });
            break;
          default:
            dispatch({
              type: 'SET_RETURN_ERROR',
              payload: _t(`${baseTranslation}.error.returned.4`, {
                type: giftcardTypeDefiniteLabel(state.giftCard?.type).toLocaleLowerCase(),
              }),
            });
            break;
        }
        return;
      } else {
        console.error('Unknown error structure:', error);
      }

      toast(({ closeToast }) => (
        <Snackbar type="danger" label={_t(`${baseTranslation}.error.returnError`)} onClose={closeToast} />
      ));

      dispatch({ type: 'SET_MODAL_VIEW', payload: null });
    }
  };

  const handleSubmitCode = (e) => {
    e.preventDefault();
    dispatch({ type: 'SET_CODE_ERROR', payload: '' });

    if (!state.codeInput.value)
      return dispatch({ type: 'SET_CODE_ERROR', payload: _t(`${baseTranslation}.error.emptyError`) });
    dispatch({ type: 'SET_LOADING' });

    (async () => {
      const res = await getGiftCard(state.codeInput.value);
      const expiryDate = new Date(res.expiryDateTimeTz);
      const currentDate = new Date();

      if (expiryDate < currentDate) {
        dispatch({
          type: 'SET_GIFTCARD',
          payload: {
            giftcard: res,
            codeError: _t(`${baseTranslation}.error.expired`, {
              type: giftcardTypeLabel(res.type).toLocaleLowerCase(),
            }),
          },
        });
        return;
      }

      if (res.error) {
        dispatch({
          type: 'SET_GIFTCARD',
          payload: { giftcard: null, codeError: _t(`${baseTranslation}.error.wrongCode`) },
        });
        return;
      }

      dispatch({ type: 'SET_GIFTCARD', payload: { giftcard: res, codeError: '' } });
    })();
  };

  return { ...state, dispatch, handleChangeCode, handleChangeSsn, handleSendReceipt, handleRefund, handleSubmitCode };
};

export default useGiftcardSelfServe;
